import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { VARIOUS_ID } from 'materiel/constants/MaterielConstants'
import { H_MAT_VARIOUS } from '../../../../account/constants/AccessRulesConstants'
import FilePanel from '../../../../components/file/FilePanel'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import VariousMaterielAction from '../actions/VariousMaterielAction'
import VariousMaterielPanel from './VariousMaterielPanel'

const VariousMaterielApp = () => {
    const {
        variousMateriel: storeVariousMateriel,
        variousMaterielTypes,
        variousMaterielSituations,
        variousMaterielPictures,
        variousMaterielFiles,
        variousMateriels,
        variousMaterielsLastSituations,
        materielSettingRules,
    } = useSelector(store => ({
        variousMateriel: store.VariousMaterielReducer.variousMateriel,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
        variousMaterielSituations: store.VariousMaterielReducer.variousMaterielSituations,
        variousMaterielPictures: store.VariousMaterielReducer.variousMaterielPictures,
        variousMaterielFiles: store.VariousMaterielReducer.variousMaterielFiles,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
        variousMaterielsLastSituations: store.VariousMaterielReducer.variousMaterielsLastSituations,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const [variousMateriel, setVariousMateriel] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const fetchVariousMateriel = id => dispatch(VariousMaterielAction.fetchVariousMateriel(id)).then(json => {
        if (json.id) {
            dispatch(VariousMaterielAction.fetchVariousMaterielSituations(json.id))
            dispatch(VariousMaterielAction.fetchVariousMaterielPictures(json.id))
            dispatch(VariousMaterielAction.fetchVariousMaterielFiles(json.serialNumber))
            setVariousMateriel({ ...json })
        }
    })

    const changeAdministrator = administrator => setVariousMateriel(prev => ({ ...prev, administrator }))

    const changeStateAttribute = changes => setVariousMateriel(prev => ({ ...prev, ...changes }))

    const deleteSituation = id => dispatch(VariousMaterielAction.deleteVariousMaterielSituation(id, params.id))

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !storeVariousMateriel.id) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === VARIOUS_ID)
                    if (isSaveBlocked(variousMateriels, variousMateriel, rules, 'materielType')) return

                    dispatch(VariousMaterielAction.createVariousMateriel(variousMateriel)).then(id => {
                        if (id) {
                            const situation = {
                                idVarious: id,
                                situationDate: variousMateriel.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(VariousMaterielAction.saveVariousMaterielSituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/variousMateriel'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === VARIOUS_ID)
                    if (isSaveBlocked(variousMateriels, variousMateriel, rules, 'materielType')) return

                    dispatch(VariousMaterielAction.updateVariousMateriel(variousMateriel)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    fetchVariousMateriel(params.id)
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(VariousMaterielAction.deleteVariousMateriel(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, variousMateriel, storeVariousMateriel, variousMateriels, materielSettingRules])

    useTitle(() => {
        const { reference = '', serialNumber = '', materielType, id } = storeVariousMateriel
        const title = reference || serialNumber || id
        const type = (variousMaterielTypes.find(t => t.code === parseInt(materielType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.variousMateriels,
                href: 'materiel/variousMateriel',
            },
            {
                title: type,
                href: `materiel/variousMateriel/${params.id}`,
            },
            {
                title,
                href: `materiel/variousMateriel/${params.id}`,
            },
        ])
    }, [storeVariousMateriel, variousMaterielTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_VARIOUS)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (!variousMateriels.length) {
            dispatch(VariousMaterielAction.fetchVariousMateriels())
        }
        if (!variousMaterielTypes.length) {
            dispatch(VariousMaterielAction.fetchVariousMaterielTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(VariousMaterielAction.resetVariousMateriel())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchVariousMateriel(params.id)
        } else {
            setIsEditMode(true)
        }
    }, [params.id])

    const getMaterielNumber = ({ serialNumber, reference }) => serialNumber || reference || ''

    const getVariousMaterielLabel = mat => {
        const number = getMaterielNumber(mat)
        if (mat.variousMaterielType) {
            const type = variousMaterielTypes.find(t => t.id === mat.variousMaterielType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getVariousMaterielFormat = () => variousMateriels
        .filter(({ isEquipment }) => !isEquipment)
        .map(mat => ({
            id: mat.id,
            label: getVariousMaterielLabel(mat),
        }))

    const disabled = { active: isEditMode, disabled: !isEditMode }

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={variousMaterielSituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <VariousMaterielPanel
                                    variousMateriel={variousMateriel}
                                    onChange={changeStateAttribute}
                                    {...disabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={storeVariousMateriel.updateDate}
                                updateLogin={storeVariousMateriel.updateLogin}
                                jobExecId={storeVariousMateriel.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={variousMateriel.administrator}
                                        isActive={!storeVariousMateriel.isEquipment && isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={variousMaterielSituations} />
                            <SmallPicturePanel pictures={variousMaterielPictures} />
                            <FilePanel files={variousMaterielFiles} />
                        </Stack>
                    </Grid>
                    {
                        !storeVariousMateriel.isEquipment && (
                            <Grid size={12}>
                                <SituationPanel
                                    materielSituation={variousMaterielSituations}
                                    active={isEditMode}
                                    idMateriel={storeVariousMateriel.id}
                                    serialNumber={storeVariousMateriel.serialNumber}
                                    purchaseDate={storeVariousMateriel.purchaseDate}
                                    idKey={'idVarious'}
                                    lastSituations={variousMaterielsLastSituations}
                                    materials={getVariousMaterielFormat()}
                                    saveFunction={situation => dispatch(VariousMaterielAction.saveVariousMaterielSituation(situation))}
                                    deleteFunction={deleteSituation}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default VariousMaterielApp
